import { BdGraphStateMap, GraphNodeState } from '../../../../lib/graph/graph.model';
import * as deepEqual from 'fast-deep-equal';
import { uuid } from '../../../shared/uuid';
import { classToClass } from 'class-transformer';
import { omit, isEmpty } from 'lodash';
import { LabelsService } from '../../../story/labels.service';
import { calculateLeafStateMap } from 'src/lib/graph/leaf-state';
import { ErrandType } from '../../../story/models/errands/errand-type.enum';
import { calculateMiniminumSynchronization } from './minimum-synchronization';
import { renamespaceValue, renamespaceKeys } from '../renamespace-utils';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import * as i0 from "@angular/core";
import * as i1 from "../../../story/labels.service";
// next human engine version
function getRecipes() {
    if (Human.scene.colorRecipes) {
        return Human.scene.colorRecipes.getRecipes();
    }
    else {
        return [];
    }
}
// rules for propagating state up our BdGraph
class GraphNodeSync {
    constructor(recipes) {
        this.shown = 'majorityWins';
        this.selected = 'majorityWins';
        this.pickable = 'majorityWins';
        recipes.forEach((recipeName) => {
            this[recipeName] = 'majorityWins';
        });
    }
}
export class SerializeChapterService {
    constructor(labelService) {
        this.labelService = labelService;
    }
    // Query the synchronization and produce the minimum graph synchronization object
    getMinimumGraphSync(graph, sync, namespaceMap) {
        // calculate leaf state based on synchronization
        // TODO: when are recipes defined?
        const recipes = getRecipes();
        const properties = Object.keys(new GraphNodeState(recipes)); // ["shown", "selected", "pickable"]
        // calculate the state of all leaves given the sync object.
        // leaf state is needed to then propagate back up the tree to build the min sync.
        const leafState = calculateLeafStateMap(graph, sync, properties);
        // minimize sync with inheritable properties: calculate graph state via upward propagation of leaf state
        const syncRules = JSON.parse(JSON.stringify(new GraphNodeSync(recipes)));
        const minSync = calculateMiniminumSynchronization(graph, syncRules, leafState);
        // copy over non-inheritable properties
        Object.entries(sync).forEach(([objectId, objectState]) => {
            const diffSync = omit(objectState, properties);
            if (!isEmpty(diffSync)) {
                minSync[objectId] = Object.assign({}, minSync[objectId] || {}, diffSync);
            }
        });
        const renamedMinSync = new BdGraphStateMap();
        Object.entries(minSync).forEach(([id, syncState]) => {
            renamedMinSync[this.switchNamespace(id, namespaceMap)] = syncState;
        }, this);
        return renamedMinSync;
    }
    // Convert synchronization object to chapter properties
    convertGraphSync(sync) {
        const showObjects = {};
        const selectObjects = {};
        const pickThroughObjects = {};
        const colorRecipeObjects = {};
        const synchronization = {};
        const recipes = getRecipes();
        Object.entries(sync).forEach(([id, stateMap]) => {
            Object.entries(stateMap).forEach(([property, value]) => {
                if (property === 'shown') {
                    showObjects[id] = value;
                }
                else if (property === 'selected') {
                    selectObjects[id] = value;
                }
                else if (property === 'pickable') {
                    pickThroughObjects[id] = !value;
                }
                else if (recipes.find(recipeName => recipeName === property)) {
                    colorRecipeObjects[property] = Object.assign({}, colorRecipeObjects[property], { [id]: value });
                }
                else {
                    // keep in the synchronization object directly
                    synchronization[id] = Object.assign({}, synchronization[id] || {}, { [property]: value });
                }
            }, this);
        }, this);
        return {
            showObjects: showObjects,
            selectObjects: selectObjects,
            pickThroughObjects: pickThroughObjects,
            colorRecipeObjects: colorRecipeObjects,
            synchronization: synchronization
        };
    }
    switchNamespace(id, namespaceMap) {
        return namespaceMap[id] || id;
    }
    // Serialize timeline maps
    serializeTimelineMaps(chapter, namespaceMap) {
        const timelineMaps = chapter.timelineMap;
        timelineMaps.forEach((tmap) => {
            tmap.data.timeline = this.switchNamespace(tmap.data.timeline, namespaceMap);
        });
        return timelineMaps;
    }
    // serialize engine properties - Only non-default properties 
    serializeProperties(huChapter) {
        const properties = {};
        const defaults = Human.properties.getDefaults();
        Object.entries(huChapter.properties).forEach(([property, value]) => {
            if (!deepEqual(value, defaults[property])) {
                properties[property] = value;
            }
        }, this);
        // legacy properties to eventually be discarded
        const annotationLayout = Human.properties.properties['annotations.layout.type'];
        if (annotationLayout !== 'draggable') {
            properties['annotations.layout.type'] = annotationLayout;
        }
        return properties;
    }
    // serialize labels and hotspots
    serializeHotspots(chapter, reverseNamespaces) {
        let hotspots = [...chapter.hotspots];
        let customData = {};
        chapter.labels.forEach((label) => {
            const labelData = this.labelService.serializeLabel(label, hotspots.length, reverseNamespaces);
            hotspots = [...hotspots, ...labelData.hotspots];
            customData = Object.assign({}, customData, labelData.data);
        });
        return {
            customData: customData,
            hotspots: hotspots
        };
    }
    // serialize background
    serializeBackground(background) {
        if (!!background) {
            return classToClass(background);
        }
        else {
            return {};
        }
    }
    // serialize ui
    serializeUI(huChapter, ui) {
        if (isEmpty(ui)) {
            return { play: { count: 1 } };
        }
        else {
            return ui;
        }
    }
    // serialize chapter model into chapter data
    serializeChapter(graph, chapter, inplace = false, namespaceMap, replaceObjectId = false) {
        const previousLabelNamespaces = chapter.labels.map(label => label.objectId);
        if (replaceObjectId) {
            // Iterate through labels and replace object IDS with new namespaces
            chapter.labels.forEach(label => {
                label.objectId = renamespaceValue(label.objectId, namespaceMap);
                label.states.forEach(state => {
                    state.errands.forEach(errand => {
                        switch (errand.type) {
                            case ErrandType.flyToChapter:
                            case ErrandType.flyToObject:
                                errand.value = renamespaceValue(errand.value, namespaceMap);
                        }
                    });
                });
            });
        }
        // for all labels, make a reverse namespace map
        const reverseNamespaces = replaceObjectId
            ? previousLabelNamespaces.reduce((acc, labelObjectId) => {
                const newNamespace = renamespaceValue(labelObjectId, namespaceMap);
                const oldNamespace = labelObjectId;
                return Object.assign({}, acc, { [newNamespace]: oldNamespace });
            }, {})
            : undefined;
        // Hotspots are generated from labels, so we perform objectID conversion on labels before
        // generating the hotspots. 
        const hotspotData = this.serializeHotspots(chapter, reverseNamespaces);
        const customData = Object.assign({}, chapter.customData, hotspotData.customData);
        const chapterData = {
            id: inplace ? chapter.id : uuid.idWithPrefix('chapter'),
            displayName: chapter.info.displayName,
            description: chapter.info.description,
            [chapter.camera.action]: chapter.camera.location,
            background: this.serializeBackground(chapter.background),
            hotspots: hotspotData.hotspots,
            clippingPlanes: chapter.clippingPlanes,
            ui: this.serializeUI(chapter, chapter.ui),
            graph: chapter.graph || {},
            timelineMap: this.serializeTimelineMaps(chapter, namespaceMap),
            customData: customData,
            viewModes: chapter.viewModes,
            lights: chapter.lights,
            recipes: chapter.recipes || {},
            transparencyMode: chapter.transparencyMode || '',
            fallbackTransparency: chapter.fallbackTransparency || '',
            properties: this.serializeProperties(chapter),
            effects: chapter.effects || {},
            options: chapter.options,
            environment: chapter.environment || {}
        };
        // get data for restoring graph state
        const minSync = this.getMinimumGraphSync(graph, chapter.synchronization, namespaceMap);
        Object.entries(this.convertGraphSync(minSync))
            .forEach(([key, val]) => {
            chapterData[key] = val;
        });
        if (replaceObjectId) {
            // Iterate through chapter properties and change the keys to new namespaces
            // e.g.: "showObjects": { "test_alzheimers_disease_brain_cross_section-root": true },
            ['showObjects', 'selectObjects', 'pickThroughObjects', 'colorRecipeObjects', 'synchronization'].forEach(value => {
                if (chapterData.hasOwnProperty(value)) {
                    chapterData[value] = renamespaceKeys(chapterData[value], namespaceMap);
                }
            });
        }
        return {
            chapter: chapterData,
            sourceId: chapter.id
        };
    }
    // serialize a single chapter from state
    serializeSingleChapter(chapter, inplace = false, namespaceMap = {}) {
        const graphNodeData = new GraphNodeState(getRecipes());
        const graph = GraphUtils.createGraphFromSrc(Object.values(Human.scene.rootObjects), 'objectId', 'objects', graphNodeData);
        return this.serializeChapter(graph, chapter, inplace, namespaceMap);
    }
    // serialize all chapters in state
    serialize(chapters, inplace = false, namespaceMap, replaceObjectId) {
        // prepare graph for graph state calculations
        const graphNodeData = new GraphNodeState(getRecipes());
        // #NOTE: [EM] This could be sped at by stashing the scene-graph on story load
        const graph = GraphUtils.createGraphFromSrc(Object.values(Human.scene.rootObjects), 'objectId', 'objects', graphNodeData);
        // serialize each chapter
        const chaptersData = [];
        const chapterIdMaps = {};
        chapters.forEach((chapter) => {
            const serialized = this.serializeChapter(graph, chapter, inplace, namespaceMap, replaceObjectId);
            chapterIdMaps[serialized.chapter.id] = serialized.sourceId;
            chaptersData.push(serialized.chapter);
        });
        return {
            chapters: chaptersData,
            sourceIdMap: chapterIdMaps // TODO: what is this???
        };
    }
}
SerializeChapterService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function SerializeChapterService_Factory() { return new SerializeChapterService(i0.ɵɵinject(i1.LabelsService)); }, token: SerializeChapterService, providedIn: "root" });
